import React, { useEffect, useMemo, useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import styled, { keyframes } from 'styled-components';
import CompetitorCard from './CompetitorCard';
import SearchAndFilters from './SearchAndFilters';
import TabsNavigation from './TabsNavigation';
import StrategyCard from './StrategyCard';
import EventsCard from './EventsCard';
import StrategicAnalysisReport from './StrategicAnalysisReport';
import MonitoringPanel from './MonitoringPanel';
import NewsTimeline from './NewsTimeline';
import AIComparativeReport from './AIComparativeReport';

import strategicReports from './data/strategicReportData';
import { strategyPoints, upcomingEvents } from './data/sidebarData';
import { listCompetitors } from '../../services/competitorsService';

import {
  CompetitorsContainer,
  CompetitorsHeader,
  CompetitorsTitle,
  CompetitorsSubtitle,
  CompetitorsGrid,
  GridItem,
} from './CompetitorsStyles';

const mono = "'JetBrains Mono', 'Consolas', monospace";
const accent = '#00B4D8';

/* ---------- inline helpers ---------- */

const ReportSelector = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
`;

const ReportLabel = styled.span`
  font-family: ${mono};
  font-size: 0.66rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-disabled);
`;

const ReportSelect = styled.select`
  background: rgba(11, 19, 32, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 5px;
  color: var(--text-primary);
  font-family: inherit;
  font-size: 0.85rem;
  padding: 0.4rem 0.7rem;
  cursor: pointer;
  outline: none;
  transition: border-color 0.18s ease;

  &:hover, &:focus { border-color: rgba(0, 180, 216, 0.4); }

  option {
    background: var(--color-tertiary);
    color: var(--text-primary);
  }
`;

const Empty = styled.div`
  padding: 1.5rem;
  text-align: center;
  color: var(--text-disabled);
  font-size: 0.9rem;
  border: 1px dashed rgba(255, 255, 255, 0.08);
  border-radius: 6px;
`;

const ErrorBox = styled(Empty)`
  color: var(--alert-error);
  border-color: rgba(239, 71, 111, 0.3);
`;

const spin = keyframes`to { transform: rotate(360deg); }`;
const Loading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  padding: 1.5rem;
  color: var(--text-disabled);
  font-family: ${mono};
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;

  &::before {
    content: '';
    width: 12px;
    height: 12px;
    border: 2px solid rgba(0, 180, 216, 0.25);
    border-top-color: ${accent};
    border-radius: 50%;
    animation: ${spin} 0.9s linear infinite;
  }
`;

const StatusStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin-top: 1rem;
`;

const StatusPill = styled.span`
  font-family: ${mono};
  font-size: 0.66rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--text-disabled);
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(11, 19, 32, 0.5);
  padding: 3px 8px;
  border-radius: 3px;

  strong {
    color: ${accent};
    margin-left: 0.4rem;
  }
`;

/* ---------- componente ---------- */

const CompetitorsAnalysis = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedReport, setSelectedReport] = useState('sanNicolas');
  const [searchQuery, setSearchQuery] = useState('');
  const [state, setState] = useState({ loading: true, error: null, items: [] });

  useEffect(() => {
    let cancelled = false;
    listCompetitors('san-nicolas')
      .then((items) => {
        if (!cancelled) setState({ loading: false, error: null, items });
      })
      .catch((err) => {
        if (!cancelled)
          setState({
            loading: false,
            error: friendlyError(err, 'No se pudieron cargar los competidores.'),
            items: [],
          });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return state.items;
    return state.items.filter((c) => {
      const hay = `${c.name} ${c.party} ${c.leaders || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [state.items, searchQuery]);

  const renderOverview = () => {
    if (state.loading) return <Loading>Cargando competidores</Loading>;
    if (state.error) return <ErrorBox>No se pudieron cargar los competidores · {state.error}</ErrorBox>;
    if (filtered.length === 0) return <Empty>No hay competidores que coincidan con la búsqueda.</Empty>;

    return (
      <CompetitorsGrid>
        <GridItem span={8} className="mobile-full-width">
          {filtered.map((c) => (
            <CompetitorCard key={c.id || c.slug} competitor={c} />
          ))}
        </GridItem>

        <GridItem span={4} className="mobile-full-width">
          <StrategyCard strategyPoints={strategyPoints} />
          <EventsCard events={upcomingEvents} />
        </GridItem>
      </CompetitorsGrid>
    );
  };

  const renderTab = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'comparison':
        return (
          <CompetitorsGrid>
            <GridItem span={12}>
              <AIComparativeReport
                competitors={state.items}
                myCandidateName="Federico Chouhy (LLA)"
              />
            </GridItem>
          </CompetitorsGrid>
        );
      case 'strategy':
        return (
          <>
            <ReportSelector>
              <ReportLabel>Informe</ReportLabel>
              <ReportSelect
                value={selectedReport}
                onChange={(e) => setSelectedReport(e.target.value)}
              >
                <option value="sanNicolas">Caso San Nicolás</option>
                <option value="generic">Genérico</option>
              </ReportSelect>
            </ReportSelector>
            <StrategicAnalysisReport report={strategicReports[selectedReport]} />
          </>
        );
      case 'monitoring':
        return <MonitoringPanel />;
      case 'history':
        return <NewsTimeline />;
      default:
        return null;
    }
  };

  return (
    <CompetitorsContainer>
      <CompetitorsHeader>
        <CompetitorsTitle>Análisis de la contienda · San Nicolás</CompetitorsTitle>
        <CompetitorsSubtitle>
          Evaluación estratégica de competidores políticos para el Concejo Deliberante.
        </CompetitorsSubtitle>
        <StatusStrip>
          <StatusPill>
            Competidores seguidos <strong>{state.items.length}</strong>
          </StatusPill>
          <StatusPill>
            Monitoreo <strong>activo</strong>
          </StatusPill>
          <StatusPill>
            Se actualiza <strong>cada semana</strong>
          </StatusPill>
        </StatusStrip>
      </CompetitorsHeader>

      <SearchAndFilters
        onSearch={setSearchQuery}
        onFilter={() => {}}
        onAddNew={() => {}}
      />

      <TabsNavigation activeTab={activeTab} setActiveTab={setActiveTab} />

      {renderTab()}
    </CompetitorsContainer>
  );
};

export default CompetitorsAnalysis;
